import type { InstantWinPrize } from '@/types'

import {
  InstantWinPrizeTier,
  type TInstantWinPrizeTierValue,
} from './helper-objects'

/**
 * The sort order weighting for prize tiers. Lower values appear first.
 */

type ExcludeNonWinner = Exclude<TInstantWinPrizeTierValue, 99>
const PRIZE_TIER_ORDER: Record<ExcludeNonWinner, number> = {
  [InstantWinPrizeTier.SS]: 0,
  [InstantWinPrizeTier.S]: 1,
  [InstantWinPrizeTier.A]: 2,
  [InstantWinPrizeTier.B]: 3,
  [InstantWinPrizeTier.C]: 4,
  [InstantWinPrizeTier.D]: 5,
  [InstantWinPrizeTier.E]: 6,
}

export type PrizeSortingOptions = {
  /**
   * Determines if availability should be a factor in sorting. If `true`,
   * Prizes that aren't available are moved to the end of the array.
   */
  availability?: boolean
  /* Determines if the prizes' tier should be a factor in sorting. Higher tier
  prizes appear earlier in the sorted array */
  prizeTier?: boolean
  /* Determines if the alternative cash value should be a factor in sorting. */
  cashAltValue?: boolean
}
/**
 * Sorts an array of prizes by their availabilty, tier and value. The sorting
 * priority is: `availability`, then `prizeTier` then `cashAltValue`.
 *
 * You can configure whether a certain sorting criteria is applied using the
 * `sortOptions` argument. For examaple, passing `{ availability: false }` will
 * change the sorting priority to: `prizeTier` then `cashAltValue`.
 *
 * @param prizes The prize list to sort. The array will be mutated
 * @param sortOptions Optional sorting criteria.
 * @returns A reference to the array
 */
export const sortPrizes = (
  prizes: InstantWinPrize[],
  sortOptions = {} as PrizeSortingOptions
) => {
  const {
    prizeTier: sortByPrizeTier = true,
    availability: sortByAvailability = true,
    cashAltValue: sortByCashAlternativeValue = true,
  } = sortOptions

  prizes.sort((a, b) => {
    if (sortByAvailability) {
      // First determine if all tickets for this prize have been won. If they have
      // we move the prize to the end of the list.
      const aUnavailable = a.prizes_available_count - a.prizes_won_count < 1
      const bUnavailable = b.prizes_available_count - b.prizes_won_count < 1
      if (aUnavailable !== bUnavailable) {
        return aUnavailable ? 1 : -1
      }
    }

    if (sortByPrizeTier) {
      // Now order the prize based on its `prize tier`
      if (a.prize_tier !== b.prize_tier) {
        const tierA = a.prize_tier
          ? PRIZE_TIER_ORDER[a.prize_tier as ExcludeNonWinner]
          : Number.MAX_SAFE_INTEGER
        const tierB = b.prize_tier
          ? PRIZE_TIER_ORDER[b.prize_tier as ExcludeNonWinner]
          : Number.MAX_SAFE_INTEGER
        return tierA - tierB
      }
    }

    if (sortByCashAlternativeValue) {
      // Finally, order based on the value of the prize
      if (a.cash_alt_value !== b.cash_alt_value) {
        return (b.cash_alt_value ?? 0) - (a.cash_alt_value ?? 0)
      }
    }

    return 0
  })
  return prizes
}

/**
 * Determines if a winning prize is worthy of a fanfare or not.
 */
export const isFanfarePrize = (prize: InstantWinPrize): boolean => {
  const { prize_tier } = prize
  if (prize_tier === undefined) {
    return false
  }
  return prize_tier !== InstantWinPrizeTier.E
}
